import { createDomain, sample, combine } from 'effector'
import { createGate } from 'effector-react'
import { entityBookingCarModel, FormValues } from '~/entities/BookingCar'
import { rentalContractModel } from '~/entities/RentalContract'
import { AxiosErrorType, RentalContactCreationRequest } from '~/shared/api'
import { backFx } from '~/shared/lib/history'
import { mapMessageErrors } from '~/shared/lib/mapMessageErrors'
import { notificationModalOpen } from '~/shared/lib/notificationModal'
import { isString } from '~/shared/lib/utils'
import { promoCodeModel } from './PromoCode'

export const domain = createDomain('features.bookingCars.bookingCar')
export const Gate = createGate<{ id: UniqueId }>()

export const $id = domain
  .createStore<UniqueId | null>(null)
  .on(Gate.state, (_, { id }) => id)

export const $bookingCar = combine(
  $id,
  entityBookingCarModel.$bookingCarsCache,
  (id, cache) => {
    if (!id) return null
    return cache.map[id] ?? null
  },
)

sample({
  clock: $id,
  filter: isString,
  target: entityBookingCarModel.requestFx,
})

export const rentalContactCreationFormSubmit = domain.createEvent<{
  formValues: FormValues
  closeDrawer: () => void
}>()

export const rentalContactCreationRequestFx = domain.createEffect<
  {
    bookingCarId: UniqueId
    closeDrawer: () => void
    formValues: FormValues
  },
  void,
  AxiosErrorType
>({
  async handler({
    bookingCarId,
    formValues: {
      workSchedule,
      dayOff,
      rentalServices,
      quantityRentalPayments,
      promocode,
    },
    closeDrawer,
  }) {
    const rentalContactCreationRequest = new RentalContactCreationRequest({
      workSchedule: workSchedule.id,
      dayOff: dayOff?.id || dayOff,
      rentalServices: Object.entries(rentalServices || {})
        .filter(([, isSelected]) => isSelected)
        .map(([id]) => id),
      quantityRentalPayments: Number(quantityRentalPayments.id),
      promocode,
    })

    if (isString(bookingCarId))
      rentalContactCreationRequest.setBookingCar(bookingCarId)

    await rentalContactCreationRequest.save()
    closeDrawer()
  },
})

sample({
  clock: rentalContactCreationFormSubmit,
  source: $id,
  filter: isString,
  fn: (bookingCarId, entries) => ({
    ...entries,
    bookingCarId,
  }),
  target: rentalContactCreationRequestFx,
})

const goToBookingCarsFx = domain.createEffect({
  handler() {
    backFx()
  },
})

sample({
  clock: rentalContactCreationRequestFx.doneData,
  fn() {
    return {
      title: 'Автомобиль успешно забронирован',
      content: (
        <div>
          Договор появится в приложение в течение 10 минут. Подпишите договор в
          приложении и в течение 2 часов приезжайте в офис для получения
          автомобиля.
          {/*<br />*/}
          {/*ВАЖНО: пройдите обучение водителей перед началом работы по*/}
          {/*<Button*/}
          {/*  href='https://t.me/taxi369_education_bot'*/}
          {/*  target='_blank'*/}
          {/*  variant='text'*/}
          {/*  sx={{ p: 0 }}*/}
          {/*>*/}
          {/*  ссылке*/}
          {/*</Button>*/}
        </div>
      ),
      variant: 'success' as const,
    }
  },
  target: [
    notificationModalOpen,
    rentalContractModel.getRentalContractSilentFx,
    goToBookingCarsFx,
    promoCodeModel.promoCodeRemoved,
  ],
})

sample({
  clock: rentalContactCreationRequestFx.failData,
  fn(e) {
    return {
      title: 'Не удалось забронировать автомобиль',
      description: mapMessageErrors(e),
      variant: 'error' as const,
    }
  },
  target: notificationModalOpen,
})
